import type { PostHogConfig } from "posthog-js";

/**
 * VITE_POSTHOG_KEY: PostHog project API key.
 * If unset, PostHog is skipped gracefully — no errors thrown.
 */
export const POSTHOG_KEY: string | undefined =
  import.meta.env.VITE_POSTHOG_KEY as string | undefined;

if (!POSTHOG_KEY) {
  console.warn(
    "[PostHog] VITE_POSTHOG_KEY is not set — analytics and session recording are disabled.",
  );
}

/**
 * PostHog options object for use with <PostHogProvider options={posthogOptions}>.
 *
 * Key decisions:
 *   - defaults: "2026-01-30" enables capture_pageview: "history_change" which
 *     intercepts history.pushState — this is what Wouter uses for navigation,
 *     so every route transition produces a distinct $pageview event.
 *   - person_profiles: "identified_only" — app requires login, no anonymous profiles.
 *   - sanitize_properties: strips URL query params from $current_url to avoid
 *     leaking tokens or search terms in event properties.
 *   - session_recording.maskTextSelector: "[data-sensitive]" — belt-and-suspenders
 *     masking of financial data in session replays beyond ph-no-capture.
 */
export const posthogOptions: Partial<PostHogConfig> = {
  api_host: "https://us.i.posthog.com",
  defaults: "2026-01-30",
  person_profiles: "identified_only",
  autocapture: true,
  session_recording: {
    maskTextSelector: "[data-sensitive]",
  },
  sanitize_properties: (properties, _event_name) => {
    if (properties?.$current_url) {
      try {
        const url = new URL(properties.$current_url as string);
        url.search = "";
        properties.$current_url = url.toString();
      } catch {
        // ignore malformed URLs
      }
    }
    return properties;
  },
};
